import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [language, setLanguage] = useState("EN");
  const [currency, setCurrency] = useState("USD");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Factory Suppliers", href: "#suppliers" },
    { label: "🏭 USA Wholesale $6K+", href: "#wholesale", highlight: true },
    { label: "🇨🇦 Canada Wholesale $6K+", href: "#canada-wholesale", highlight: true },
    { label: "Influencer Hub", href: "#influencers" },
    { label: "Education", href: "#education" },
    { label: "🇺🇸 Local Warehouse Shop", href: "#overseas-warehouse" },
    { label: "🇨🇦 Canada Warehouse Shop", href: "#canada-overseas-warehouse" },
    { label: "USA Warehouse", href: "#usa-warehouse" },
    { label: "Canada Warehouse", href: "#canada-warehouse" },
  ];

  return (
    <>
      {/* Top announcement bar */}
      <div className="bg-[#C41E3A] text-white text-xs text-center py-2 px-4">
        <span className="font-medium">FREE SHIPPING on orders over $50 to USA & Canada</span>
        <span className="mx-3">|</span>
        <span>Secure USD Payments &bull; Verified Suppliers &bull; Fast Delivery</span>
      </div>

      {/* Main Navbar */}
      <nav
        className="sticky top-0 z-50 transition-all duration-300"
        style={scrolled
          ? { background: "#fff", boxShadow: "0 2px 16px rgba(212,175,55,0.15)" }
          : { background: "linear-gradient(135deg, #b8860b 0%, #D4AF37 40%, #f5d060 70%, #c9a227 100%)" }
        }
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 cursor-pointer whitespace-nowrap">
              <img
                src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
                alt="Crown Prime Mall Logo"
                className="h-10 w-10 rounded-full object-cover"
              />
              <div>
                <div className={`font-bold text-base leading-tight ${scrolled ? "text-[#C41E3A]" : "text-[#1a0a00]"}`}>
                  Crown Prime Mall
                </div>
                <div className={`text-[10px] leading-tight font-semibold ${scrolled ? "text-gray-500" : "text-[#7a3a00]"}`}>
                  USA &amp; Canada Marketplace
                </div>
              </div>
            </a>

            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-xl mx-6">
              <div className={`flex w-full rounded-full overflow-hidden border-2 ${scrolled ? "border-[#D4AF37]" : "border-[#7a5c00]/50"}`}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products, suppliers..."
                  className="flex-1 px-4 py-2 text-sm text-gray-800 bg-white outline-none"
                />
                <button className="bg-[#C41E3A] px-5 text-white hover:bg-[#a01830] transition-colors cursor-pointer">
                  <i className="ri-search-line text-sm"></i>
                </button>
              </div>
            </div>

            {/* Right Controls - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              {/* Language */}
              <button
                onClick={() => setLanguage(language === "EN" ? "FR" : "EN")}
                className={`text-xs font-semibold px-2 py-1 rounded border cursor-pointer whitespace-nowrap transition-colors ${
                  scrolled ? "border-gray-300 text-gray-700 hover:border-[#C41E3A]" : "border-[#7a5c00] text-[#1a0a00] bg-white/30 hover:bg-white/50"
                }`}
              >
                <i className="ri-global-line mr-1"></i>{language}
              </button>
              {/* Currency */}
              <button
                onClick={() => setCurrency(currency === "USD" ? "CAD" : "USD")}
                className={`text-xs font-semibold px-2 py-1 rounded border cursor-pointer whitespace-nowrap transition-colors ${
                  scrolled ? "border-gray-300 text-gray-700 hover:border-[#C41E3A]" : "border-[#7a5c00] text-[#1a0a00] bg-white/30 hover:bg-white/50"
                }`}
              >
                {currency}
              </button>
              {/* WhatsApp */}
              <a
                href="https://wa.me/1234567890"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 bg-[#25D366] text-white text-xs px-3 py-2 rounded-full font-semibold hover:bg-[#1ebe5d] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                <span>WhatsApp</span>
              </a>
              {/* Cart */}
              <button className={`relative cursor-pointer ${scrolled ? "text-gray-700" : "text-[#1a0a00]"}`}>
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-shopping-cart-2-line text-xl"></i>
                </div>
                <span className="absolute -top-1 -right-1 bg-[#C41E3A] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">3</span>
              </button>
            </div>

            {/* Mobile Hamburger */}
            <button
              className={`md:hidden cursor-pointer ${scrolled ? "text-gray-800" : "text-[#1a0a00]"}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
              </div>
            </button>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6 pb-2">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  (link as { highlight?: boolean }).highlight
                    ? "text-[#C41E3A] font-extrabold"
                    : scrolled ? "text-gray-700 hover:text-[#C41E3A]" : "text-[#1a0a00] hover:text-[#7a3a00]"
                }`}
              >
                {link.label}
              </a>
            ))}
            <div className="ml-auto flex items-center gap-2 pb-1">
              <Link
                to="/products"
                className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap font-semibold ${scrolled ? "text-white bg-[#D4AF37] hover:bg-[#b8962e]" : "text-[#D4AF37] bg-[#1a0a00] hover:bg-[#2a1400]"}`}
              >
                <i className="ri-store-2-line"></i> All Products
              </Link>
              <Link
                to="/crownchat"
                className="flex items-center gap-1 text-xs text-white bg-[#C41E3A] px-3 py-1 rounded-full hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap font-semibold"
              >
                <i className="ri-message-3-line"></i> CrownChat
              </Link>
              <Link
                to="/supplier-register"
                className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap font-semibold ${scrolled ? "text-[#D4AF37] border border-[#D4AF37] hover:bg-[#D4AF37]/10" : "text-white border border-[#1a0a00] bg-white/20 hover:bg-white/40"}`}
              >
                <i className="ri-building-2-line"></i> Become a Supplier
              </Link>
              <Link
                to="/factory/login"
                className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap font-semibold ${scrolled ? "text-white bg-[#1a0a0a] border border-white/30 hover:bg-[#333]" : "text-[#1a0a00] bg-white/40 border border-[#7a5c00]/40 hover:bg-white/60"}`}
              >
                <i className="ri-building-4-line"></i> Factory Login
              </Link>
              <Link
                to="/reseller/login"
                className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap font-semibold ${scrolled ? "text-white bg-[#C41E3A] hover:bg-[#a01830]" : "text-white bg-[#C41E3A]/90 hover:bg-[#C41E3A]"}`}
              >
                <i className="ri-star-line"></i> Reseller Login
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#7a5c00]/30 px-4 py-4" style={{ background: "linear-gradient(135deg, #b8860b 0%, #D4AF37 60%, #f5d060 100%)" }}>
            <div className="flex mb-4">
              <input
                type="text"
                placeholder="Search products..."
                className="flex-1 px-3 py-2 text-sm rounded-l-full outline-none"
              />
              <button className="bg-[#C41E3A] px-4 text-white rounded-r-full">
                <i className="ri-search-line"></i>
              </button>
            </div>
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-[#1a0a00] font-semibold py-2 text-sm border-b border-[#7a5c00]/20 cursor-pointer hover:text-[#7a3a00]"
              >
                {link.label}
              </a>
            ))}
            <div className="flex flex-wrap gap-3 mt-4">
              <button onClick={() => setLanguage(language === "EN" ? "FR" : "EN")} className="text-xs text-[#1a0a00] font-bold border border-[#7a5c00] bg-white/40 px-3 py-1 rounded-full cursor-pointer whitespace-nowrap">
                {language}
              </button>
              <button onClick={() => setCurrency(currency === "USD" ? "CAD" : "USD")} className="text-xs text-[#1a0a00] font-bold border border-[#7a5c00] bg-white/40 px-3 py-1 rounded-full cursor-pointer whitespace-nowrap">
                {currency}
              </button>
              <Link
                to="/reseller/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-1 text-xs text-white bg-[#C41E3A] px-3 py-1 rounded-full font-bold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-star-line"></i> Reseller Login
              </Link>
              <Link
                to="/factory/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-1 text-xs text-[#1a0a00] bg-white/40 border border-[#7a5c00] px-3 py-1 rounded-full font-bold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-building-4-line"></i> Factory Login
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
